import React, { useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Chip,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SaveIcon from '@mui/icons-material/Save'
import {
    buildBlockingReviewAlerts,
    buildClientReadyActionItems,
    buildReviewCoverage,
    buildWordingActionItems
} from './qualityReviewSummary'

export default function ClientReadyResults({
    result,
    expectations = {},
    reviewState,
    reviewStateDirty,
    savingReviewState,
    onResolutionChange,
    onNoteChange,
    onSaveReviewState,
    onOpenDocument,
    availableSourceFileNames = [],
    availableRetainedSourceFileIds = [],
    openingSourceFileId = '',
    onConfirmCorrectClient,
    onBlockerResolutionChange,
    onBlockerNoteChange,
    autosaveEnabled = false,
    autosaveStatus = '',
    autosaveError = '',
    lastSavedAt = '',
    onRetryAutosave,
    showReviewerActions = false,
    feedbackState = {},
    onFindingFeedback,
    savingFeedback = false,
    readOnly = false,
    skippedFiles = []
}) {
    const [statusFilter, setStatusFilter] = useState('unresolved')
    const [fileFilter, setFileFilter] = useState('all')
    const [searchTerm, setSearchTerm] = useState('')
    const [groupingMode, setGroupingMode] = useState('issue')

    const checklistItems = useMemo(
        () => buildClientReadyActionItems(result, reviewState, { expectations }),
        [result, reviewState, expectations]
    )
    const wordingItems = useMemo(
        () => buildWordingActionItems(result, reviewState),
        [result, reviewState]
    )
    const allItems = [...checklistItems, ...wordingItems]
    const blockers = useMemo(() => buildBlockingReviewAlerts(result, reviewState), [result, reviewState])
    const visibleBlockers = blockers.filter(blocker =>
        !blocker.resolved || statusFilter === 'resolved' || statusFilter === 'all'
    )
    const coverage = useMemo(() => buildReviewCoverage(result, reviewState), [result, reviewState])
    const fileOptions = Array.from(new Set(allItems.flatMap(item => item.files.map(file => file.fileName)))).sort((a, b) => a.localeCompare(b))
    const normalizedSearch = searchTerm.trim().toLowerCase()
    const availableSourceFiles = new Set(availableSourceFileNames)
    const availableRetainedSourceFiles = new Set(availableRetainedSourceFileIds.map(String))
    const visibleItems = allItems.reduce((output, item) => {
        if (statusFilter === 'unresolved' && !isUnresolved(item.resolution)) return output
        if (statusFilter === 'action' && (!isUnresolved(item.resolution) || item.kind === 'manual')) return output
        if (statusFilter === 'manual' && (!isUnresolved(item.resolution) || item.kind !== 'manual')) return output
        if (statusFilter === 'resolved' && isUnresolved(item.resolution)) return output

        const itemTextMatches = normalizedSearch && [
            item.label,
            item.formalLabel,
            item.summary,
            item.note
        ].join(' ').toLowerCase().includes(normalizedSearch)
        const presentationFiles = (item.files || []).filter(file => {
            if (fileFilter !== 'all' && file.fileName !== fileFilter) return false
            if (!normalizedSearch || itemTextMatches) return true
            return [file.fileName, ...(file.locations || [])]
                .join(' ')
                .toLowerCase()
                .includes(normalizedSearch)
        })

        if (!presentationFiles.length) return output
        output.push({ ...item, files: presentationFiles })
        return output
    }, [])
    const fileGroups = useMemo(() => groupActionItemsByFile(visibleItems), [visibleItems])

    function applyResolution(item, resolution) {
        const stateKeys = item.stateKeys?.length ? item.stateKeys : [item.id]
        stateKeys.forEach(stateKey => onResolutionChange?.(stateKey, resolution, item))
    }

    function applyNote(item, note) {
        const stateKeys = item.stateKeys?.length ? item.stateKeys : [item.id]
        stateKeys.forEach(stateKey => onNoteChange?.(stateKey, note, item))
    }

    function handleQuickResolution(item, resolution) {
        applyResolution(item, resolution)
    }

    function renderActionItem(item, cardId = item.id, hideFileName = false) {
        return (
            <ActionItemCard
                key={cardId}
                cardId={cardId}
                item={item}
                hideFileName={hideFileName}
                onResolutionChange={applyResolution}
                onQuickResolution={handleQuickResolution}
                onNoteChange={applyNote}
                onOpenDocument={onOpenDocument}
                availableSourceFiles={availableSourceFiles}
                availableRetainedSourceFiles={availableRetainedSourceFiles}
                openingSourceFileId={openingSourceFileId}
                showReviewerActions={showReviewerActions}
                feedbackDecision={findingFeedbackDecision(feedbackState, item)}
                onFindingFeedback={onFindingFeedback}
                savingFeedback={savingFeedback}
                readOnly={readOnly}
            />
        )
    }

    function applyBlockerResolution(alert, resolution) {
        const callback = onBlockerResolutionChange || onResolutionChange
        callback?.(alert.stateKey || alert.id, resolution, alert)
    }

    function applyBlockerNote(alert, note) {
        const callback = onBlockerNoteChange || onNoteChange
        callback?.(alert.stateKey || alert.id, note, alert)
    }

    return (
        <Box>
            <Stack spacing={2.5}>
                {readOnly && (
                    <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
                        This result is view-only. Reviewer decisions and feedback cannot be changed.
                    </Alert>
                )}

                <ReviewExceptionsSummary
                    blockers={visibleBlockers}
                    skippedFiles={skippedFiles}
                    onConfirmCorrectClient={onConfirmCorrectClient}
                    onResolutionChange={applyBlockerResolution}
                    onNoteChange={applyBlockerNote}
                    showReviewerActions={showReviewerActions}
                    readOnly={readOnly}
                />

                <Box
                    className="quality-review-no-print"
                >
                    <Stack spacing={1.25}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                            <Box>
                                <Typography variant="h6" sx={{ color: '#173042', fontWeight: 900 }}>
                                    Issues to check
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#526b7b', mt: 0.2 }}>
                                    {coverage.filesRead} of {coverage.filesTotal} files checked · {visibleItems.length} issue{visibleItems.length === 1 ? '' : 's'} shown
                                </Typography>
                            </Box>
                            <ButtonGroup size="small" aria-label="Group results">
                                <Button
                                    variant={groupingMode === 'issue' ? 'contained' : 'outlined'}
                                    onClick={() => setGroupingMode('issue')}
                                    aria-pressed={groupingMode === 'issue'}
                                >
                                    Group by issue
                                </Button>
                                <Button
                                    variant={groupingMode === 'file' ? 'contained' : 'outlined'}
                                    onClick={() => setGroupingMode('file')}
                                    aria-pressed={groupingMode === 'file'}
                                >
                                    Group by file
                                </Button>
                            </ButtonGroup>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                        <TextField
                            size="small"
                            label="Show"
                            select
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                            sx={{ minWidth: { md: 190 } }}
                        >
                            <MenuItem value="unresolved">Unresolved only</MenuItem>
                            <MenuItem value="action">Issues only</MenuItem>
                            <MenuItem value="manual">Manual checks</MenuItem>
                            <MenuItem value="resolved">Resolved</MenuItem>
                            <MenuItem value="all">Everything</MenuItem>
                        </TextField>
                        <TextField
                            size="small"
                            label="File"
                            select
                            value={fileFilter}
                            onChange={(event) => setFileFilter(event.target.value)}
                            sx={{ minWidth: { md: 240 } }}
                        >
                            <MenuItem value="all">All files</MenuItem>
                            {fileOptions.map(fileName => (
                                <MenuItem key={fileName} value={fileName}>{fileName}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            label="Search"
                            placeholder="Checklist item, file or location"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            sx={{ flex: 1 }}
                        />
                        {(statusFilter !== 'unresolved' || fileFilter !== 'all' || searchTerm) && (
                            <Button
                                variant="text"
                                onClick={() => {
                                    setStatusFilter('unresolved')
                                    setFileFilter('all')
                                    setSearchTerm('')
                                }}
                            >
                                Clear filters
                            </Button>
                        )}
                        </Stack>
                    </Stack>
                </Box>

                {visibleItems.length === 0 ? (
                    <Alert severity="success" sx={{ borderRadius: 2.5 }}>
                        No items match this view.
                    </Alert>
                ) : (
                    groupingMode === 'issue' ? (
                        <Box sx={issueGridSx}>
                            {visibleItems.map(item => renderActionItem(item))}
                        </Box>
                    ) : (
                        <Stack spacing={1.5}>
                            {fileGroups.map(group => (
                                <Paper
                                    key={group.key}
                                    component="section"
                                    aria-labelledby={fileGroupHeadingId(group.key)}
                                    variant="outlined"
                                    sx={fileGroupSx}
                                >
                                    <Stack spacing={1.25}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} justifyContent="space-between">
                                            <Typography component="h3" variant="h6" id={fileGroupHeadingId(group.key)} sx={{ color: '#173042', fontWeight: 900, wordBreak: 'break-word' }}>
                                                {group.fileName}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#526b7b', fontWeight: 800 }}>
                                                {group.items.length} issue{group.items.length === 1 ? '' : 's'}
                                            </Typography>
                                        </Stack>
                                        <Box sx={issueGridSx}>
                                            {group.items.map(({ item, file }) => renderActionItem(
                                                { ...item, files: [file] },
                                                `${group.key}:${item.id}`,
                                                true
                                            ))}
                                        </Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    )
                )}

                {!readOnly && <Stack
                    className="quality-review-no-print"
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.25}
                    justifyContent="flex-end"
                    alignItems={{ sm: 'center' }}
                    sx={{ pt: 1, borderTop: '1px solid #e5ecef' }}
                >
                    <AutosaveStatus
                        enabled={autosaveEnabled}
                        status={autosaveStatus}
                        error={autosaveError}
                        dirty={reviewStateDirty}
                        saving={savingReviewState}
                        lastSavedAt={lastSavedAt}
                        onRetry={onRetryAutosave || onSaveReviewState}
                    />
                    {!autosaveEnabled && (
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={onSaveReviewState}
                            disabled={!reviewStateDirty || savingReviewState}
                        >
                            {savingReviewState ? 'Saving…' : 'Save progress'}
                        </Button>
                    )}
                </Stack>}
            </Stack>
        </Box>
    )
}

function ActionItemCard({
    cardId,
    item,
    hideFileName,
    onResolutionChange,
    onQuickResolution,
    onNoteChange,
    onOpenDocument,
    availableSourceFiles,
    availableRetainedSourceFiles,
    openingSourceFileId,
    showReviewerActions,
    feedbackDecision,
    onFindingFeedback,
    savingFeedback,
    readOnly
}) {
    const [wordingExpanded, setWordingExpanded] = useState(false)
    const resolved = !isUnresolved(item.resolution)
    const showStatusChip = resolved || item.resolution === 'needs_clarification' || item.kind === 'manual'
    const headingId = actionCardHeadingId(cardId || item.id)

    return (
        <Paper
            component="article"
            variant="outlined"
            aria-labelledby={headingId}
            sx={{
                p: { xs: 1.5, md: 2 },
                height: 'fit-content',
                borderColor: resolved ? '#cfe1d6' : '#dce5ea',
                borderRadius: 2.5,
                boxShadow: '0 4px 14px rgba(31, 55, 70, 0.045)',
                backgroundColor: '#ffffff'
            }}
        >
            <Stack spacing={1.25}>
                <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: '100%', pr: 1 }}>
                    <Box
                        sx={{
                            minWidth: 32,
                            height: 32,
                            px: item.checklistNumber ? 0.75 : 0,
                            borderRadius: 99,
                            display: 'grid',
                            placeItems: 'center',
                            color: resolved ? '#356b48' : '#365569',
                            backgroundColor: resolved ? '#eaf6ee' : '#edf4f7',
                            fontWeight: 900
                        }}
                    >
                        {item.checklistNumber ? `${item.checklistNumber}.` : '•'}
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ sm: 'center' }}>
                            <Typography component="h3" id={headingId} sx={{ m: 0, fontSize: '1.08rem', lineHeight: 1.35, fontWeight: 900, color: '#1b2c39' }}>
                                {item.label}
                            </Typography>
                            {showStatusChip && <Chip
                                size="small"
                                label={item.resolution === 'needs_clarification'
                                    ? 'Need clarification'
                                    : resolved
                                        ? resolutionLabel(item.resolution, item.kind)
                                        : 'Manual check'}
                                color={resolved ? 'success' : item.kind === 'manual' ? 'default' : 'warning'}
                                variant={resolved || item.kind !== 'manual' ? 'filled' : 'outlined'}
                            />}
                        </Stack>
                    </Box>
                </Stack>

                <Stack spacing={1} sx={{ pl: { xs: 0, sm: 5.5 } }}>
                    {item.files.map(file => {
                        const locations = visibleLocations(file.locations)
                        const canOpenSource = Boolean(onOpenDocument && (
                            availableSourceFiles?.has(file.fileName) ||
                            availableRetainedSourceFiles?.has(String(file.fileId || ''))
                        ))
                        return (
                            <Box key={`${item.id}:${file.fileId || file.fileName}`}>
                                {!hideFileName && <Typography variant="body2" sx={{ display: 'block', color: '#5a7181', fontWeight: 850, wordBreak: 'break-word' }}>
                                    {file.fileName}
                                </Typography>}
                                <Stack
                                    component="ul"
                                    direction="row"
                                    spacing={0.65}
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ listStyle: 'none', m: 0, mt: hideFileName ? 0 : 0.55, p: 0 }}
                                >
                                    {locations.map(location => {
                                        const sourceLocation = (file.sourceLocations || []).find(candidate => candidate.label === location) || null
                                        return (
                                            <Box component="li" key={`${file.fileName}:${location}`}>
                                                {canOpenSource ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<OpenInNewIcon />}
                                                        onClick={() => onOpenDocument(file.fileId, file.fileName, location, sourceLocation, item.id)}
                                                        disabled={String(openingSourceFileId || '') === String(file.fileId || '')}
                                                        aria-label={`Open ${file.fileName} at ${location}`}
                                                        sx={{ minHeight: 34, borderRadius: 99, textTransform: 'none' }}
                                                    >
                                                        {location}
                                                    </Button>
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={location}
                                                        sx={{
                                                            height: 'auto',
                                                            minHeight: 34,
                                                            color: '#334e5f',
                                                            borderColor: '#cddbe3',
                                                            backgroundColor: '#f9fbfc',
                                                            '& .MuiChip-label': {
                                                                display: 'block',
                                                                py: 0.25,
                                                                whiteSpace: 'normal',
                                                                fontSize: '0.95rem'
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        )
                                    })}
                                    {locations.length === 0 && (
                                        <Box component="li">
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label="Check whole file"
                                                aria-label={`No specific location for ${file.fileName}; check whole file`}
                                                sx={{
                                                    height: 'auto',
                                                    minHeight: 34,
                                                    color: '#6b5527',
                                                    borderColor: '#dfc77d',
                                                    backgroundColor: '#fffaf0',
                                                    '& .MuiChip-label': {
                                                        display: 'block',
                                                        py: 0.25,
                                                        whiteSpace: 'normal',
                                                        fontSize: '0.95rem'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )
                    })}
                </Stack>

                {!readOnly && showReviewerActions && !resolved && (
                    <QuickTriageButtons
                        item={item}
                        onResolutionChange={onQuickResolution}
                    />
                )}

                {item.kind === 'wording' && (
                    <Box sx={{ pl: { xs: 0, sm: 5.5 } }}>
                        <Button
                            size="small"
                            aria-expanded={wordingExpanded}
                            aria-controls={`${headingId}-wording`}
                            onClick={() => setWordingExpanded(value => !value)}
                            sx={{ textTransform: 'none', px: 0 }}
                        >
                            {wordingExpanded ? 'Hide wording changes' : 'View wording changes'}
                        </Button>
                        {wordingExpanded && (
                            <Stack id={`${headingId}-wording`} spacing={1.25} sx={{ pt: 0.75 }}>
                                {(item.occurrences || []).length ? item.occurrences.map((occurrence, index) => (
                                    <Box key={occurrence.stateKey || index} sx={{ borderLeft: '2px solid #cddbe3', pl: 1.25 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#365569' }}>
                                            {occurrence.location || 'Location not saved'}
                                        </Typography>
                                        {occurrence.currentText && <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                                            <Box component="span" sx={{ fontWeight: 800 }}>Current: </Box>{occurrence.currentText}
                                        </Typography>}
                                        {occurrence.suggestedText && <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                                            <Box component="span" sx={{ fontWeight: 800 }}>Suggested: </Box>{occurrence.suggestedText}
                                        </Typography>}
                                        {(!occurrence.currentText || !occurrence.suggestedText) && <Typography variant="body2" sx={{ mt: 0.5, color: '#6b5527' }}>
                                            The wording comparison was not retained in this saved review. Run a fresh check to see the current and suggested wording.
                                        </Typography>}
                                    </Box>
                                )) : <Typography variant="body2" sx={{ color: '#6b5527' }}>
                                    The wording comparison was not retained in this saved review. Run a fresh check to see the current and suggested wording.
                                </Typography>}
                            </Stack>
                        )}
                    </Box>
                )}
                {!readOnly && showReviewerActions && (
                    <Stack spacing={1.25} sx={{ pt: 1, borderTop: '1px solid #e5ecef' }}>
                    {!readOnly && showReviewerActions && onNoteChange && (
                        <TextField
                            className="quality-review-no-print"
                            label="Reviewer note (optional)"
                            value={item.note || ''}
                            onChange={(event) => onNoteChange(item, event.target.value)}
                            multiline
                            minRows={2}
                            inputProps={{ maxLength: 1000 }}
                        />
                    )}
                    {!readOnly && showReviewerActions && onFindingFeedback && (
                        <FindingFeedbackActions
                            item={item}
                            decision={feedbackDecision}
                            onChange={onFindingFeedback}
                            disabled={savingFeedback}
                        />
                    )}
                    </Stack>
                )}
                </Stack>
        </Paper>
    )
}

function ReviewExceptionsSummary({
    blockers,
    skippedFiles,
    onConfirmCorrectClient,
    onResolutionChange,
    onNoteChange,
    showReviewerActions,
    readOnly
}) {
    const files = skippedFiles || []
    if (!blockers.length && !files.length) return null

    if (showReviewerActions) {
        return (
            <Stack spacing={1}>
                {blockers.map(alert => (
                    <BlockerAlert
                        key={alert.id}
                        alert={alert}
                        onConfirmCorrectClient={onConfirmCorrectClient}
                        onResolutionChange={onResolutionChange}
                        onNoteChange={onNoteChange}
                        showReviewerActions
                        readOnly={readOnly}
                    />
                ))}
                {files.length > 0 && (
                    <ManualReviewExceptions skippedFiles={files} />
                )}
            </Stack>
        )
    }

    return (
        <Alert
            severity="warning"
            variant="outlined"
            sx={{
                alignSelf: 'flex-start',
                width: 'fit-content',
                maxWidth: '100%',
                borderRadius: 2,
                borderColor: '#dfc77d',
                backgroundColor: '#fffaf0',
                py: 0.25,
                '& .MuiAlert-icon': { py: 0.35 },
                '& .MuiAlert-message': { py: 0.35 }
            }}
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.25, md: 1.25 }} alignItems={{ md: 'baseline' }}>
                <Typography sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}>Before sending</Typography>
                <Stack spacing={0.2}>
                    {blockers.map(alert => (
                        <Typography key={alert.id} variant="body2">
                            <strong>{alert.fileName || 'Review batch'}</strong> — {alert.title}
                        </Typography>
                    ))}
                    {files.map(file => (
                        <Typography key={`${file.name}-${file.reason}`} variant="body2">
                            <strong>{file.name}</strong> could not be fully checked{file.reason ? ` — ${file.reason}` : ''}
                        </Typography>
                    ))}
                </Stack>
            </Stack>
        </Alert>
    )
}

function ManualReviewExceptions({ skippedFiles }) {
    return (
        <Alert severity="warning" sx={{ borderRadius: 2.5, alignItems: 'flex-start' }}>
            <Typography sx={{ fontWeight: 900 }}>Check these items manually</Typography>
            <Stack spacing={0.4} sx={{ mt: 0.4 }}>
                {skippedFiles.map(file => (
                    <Typography key={`${file.name}-${file.reason}`} variant="body2">
                        <strong>{file.name}</strong> could not be fully checked{file.reason ? ` — ${file.reason}` : ''}
                    </Typography>
                ))}
            </Stack>
        </Alert>
    )
}

function BlockerAlert({
    alert,
    onConfirmCorrectClient,
    onResolutionChange,
    onNoteChange,
    showReviewerActions,
    readOnly
}) {
    const resolved = alert.resolved
    const requiresResolution = alert.blocksReview

    function confirmCorrectClient() {
        if (onConfirmCorrectClient) {
            onConfirmCorrectClient(alert)
            return
        }
        onResolutionChange(alert, 'not_applicable')
    }

    return (
        <Alert
            severity={resolved ? 'success' : alert.blocking ? 'error' : 'warning'}
            sx={{ borderRadius: 2.5, alignItems: 'flex-start' }}
        >
            <Stack spacing={1.1} sx={{ width: '100%' }}>
                <Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ sm: 'center' }}>
                        <Typography sx={{ fontWeight: 900 }}>
                            {alert.fileName || 'Review batch'}
                        </Typography>
                        <Chip
                            size="small"
                            label={resolved ? resolutionLabel(alert.resolution) : alert.blocking ? 'Review required' : 'Attention'}
                            color={resolved ? 'success' : alert.blocking ? 'error' : 'warning'}
                        />
                    </Stack>
                    {(alert.locations || []).map(location => (
                        <Typography key={location} variant="body2" sx={{ mt: 0.35, fontWeight: 800 }}>
                            {location}
                        </Typography>
                    ))}
                    <Typography variant="body2" sx={{ mt: 0.35 }}>
                        {alert.title}
                    </Typography>
                </Box>

                {!readOnly && showReviewerActions && !resolved && requiresResolution && (
                    <Box className="quality-review-no-print">
                        <TextField
                            fullWidth
                            size="small"
                            label="Resolution note"
                            placeholder="Record what you checked or changed"
                            value={alert.reviewerNote || ''}
                            onChange={(event) => onNoteChange?.(alert, event.target.value)}
                            inputProps={{ maxLength: 1000 }}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                            {alert.type === 'wrong_client' && (
                                <Button variant="outlined" onClick={confirmCorrectClient}>
                                    Confirm correct client
                                </Button>
                            )}
                            <Button
                                variant={alert.resolution === 'needs_clarification' ? 'contained' : 'outlined'}
                                onClick={() => onResolutionChange(alert, 'needs_clarification')}
                            >
                                Need clarification
                            </Button>
                            <Button
                                color="success"
                                variant="outlined"
                                disabled={!String(alert.reviewerNote || '').trim()}
                                onClick={() => onResolutionChange(alert, 'fixed')}
                            >
                                Resolve with note
                            </Button>
                        </Stack>
                    </Box>
                )}

                {!readOnly && showReviewerActions && resolved && requiresResolution && (
                    <Stack spacing={0.5}>
                        {alert.reviewerNote && (
                            <Typography variant="body2" sx={{ color: '#3f6750' }}>
                                Resolution note: {alert.reviewerNote}
                            </Typography>
                        )}
                        <Button
                            className="quality-review-no-print"
                            size="small"
                            variant="text"
                            onClick={() => onResolutionChange(alert, 'open')}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Reopen
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Alert>
    )
}

function QuickTriageButtons({ item, onResolutionChange }) {
    const actions = item.kind === 'manual'
        ? [
            { label: 'Checked — okay', resolution: 'fixed', color: 'success' },
            { label: 'Needs correction', resolution: 'open', color: 'warning' },
            { label: 'Need clarification', resolution: 'needs_clarification', color: 'inherit' }
        ]
        : [
            { label: 'Fixed', resolution: 'fixed', color: 'success' },
            { label: 'Not applicable', resolution: 'not_applicable', color: 'inherit' },
            { label: 'Need clarification', resolution: 'needs_clarification', color: 'inherit' }
        ]

    return (
        <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 1.1 }}
            onClick={(event) => event.stopPropagation()}
        >
            {actions.map(action => (
                <Button
                    key={action.resolution}
                    size="small"
                    color={action.color}
                    variant="outlined"
                    onClick={() => onResolutionChange(item, action.resolution)}
                    sx={{ minHeight: 34 }}
                >
                    {action.label}
                </Button>
            ))}
        </Stack>
    )
}

function AutosaveStatus({ enabled, status, error, dirty, saving, lastSavedAt, onRetry }) {
    if (!enabled) {
        return dirty ? (
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                Progress has not been saved yet.
            </Typography>
        ) : null
    }

    const isSaving = saving || status === 'saving'
    const hasError = status === 'error' || Boolean(error)
    if (hasError) {
        return (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Typography variant="body2" color="error.main" sx={{ fontWeight: 800 }}>
                    Autosave failed{error ? ` — ${error}` : ''}
                </Typography>
                <Button size="small" variant="outlined" color="error" onClick={onRetry}>
                    Retry
                </Button>
            </Stack>
        )
    }

    if (isSaving || dirty) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                {isSaving ? 'Saving changes…' : 'Changes queued for autosave…'}
            </Typography>
        )
    }

    return (
        <Typography variant="body2" color="success.main" sx={{ fontWeight: 800 }}>
            All changes saved{lastSavedAt ? ` · ${formatSavedTime(lastSavedAt)}` : ''}
        </Typography>
    )
}

function FindingFeedbackActions({ item, decision, onChange, disabled }) {
    const actions = [
        { label: 'Correct finding', value: 'manual_confirmed' },
        { label: 'False positive', value: 'false_positive' },
        { label: 'Unclear', value: 'unclear' }
    ]
    return (
        <Box className="quality-review-no-print" sx={{ pt: 1, borderTop: '1px solid #e5ecef' }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: '#526b7b', fontWeight: 850 }}>
                Was this finding accurate?
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {actions.map(action => (
                    <Button
                        key={action.value}
                        size="small"
                        variant={decision === action.value ? 'contained' : 'outlined'}
                        onClick={() => onChange(item, action.value)}
                        disabled={disabled}
                    >
                        {action.label}
                    </Button>
                ))}
            </Stack>
        </Box>
    )
}

function findingFeedbackDecision(feedbackState, item) {
    const decisions = Array.from(new Set((item?.files || [])
        .map(file => feedbackState?.findings?.[`finding:${file.fileId || file.fileName}:${item.id}`]?.decision)
        .filter(Boolean)))
    return decisions.length === 1 ? decisions[0] : ''
}

function resolutionLabel(value, kind = '') {
    if (value === 'fixed') return kind === 'manual' ? 'Checked — okay' : 'Fixed'
    if (value === 'not_applicable' || value === 'na') return 'Not applicable'
    if (value === 'needs_clarification') return 'Need clarification'
    if (value === 'reviewed') return 'Reviewed'
    return 'Open'
}

function isUnresolved(value) {
    return value === 'open' || value === 'needs_clarification'
}

function isUsableLocation(value) {
    const text = String(value || '').trim()
    return Boolean(text) && !/^location (?:is )?not available$/i.test(text)
}

function visibleLocations(locations) {
    const unique = Array.from(new Set((locations || [])
        .map(location => String(location || '').trim())
        .filter(isUsableLocation)))
    const pages = unique.filter(location => /^page\s+\d+$/i.test(location))
        .sort((left, right) => Number(left.match(/\d+/)?.[0]) - Number(right.match(/\d+/)?.[0]))
    const pageSet = new Set(pages)
    return [...pages, ...unique.filter(location => !pageSet.has(location))]
}

function actionCardHeadingId(itemId) {
    return `quality-action-${String(itemId || 'item').replace(/[^a-z0-9_-]+/gi, '-')}`
}

function groupActionItemsByFile(items) {
    const groups = new Map()

    for (const item of items || []) {
        for (const file of item.files || []) {
            const key = String(file.fileId || file.fileName || 'review-file')
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    fileName: file.fileName || 'Review file',
                    items: []
                })
            }
            groups.get(key).items.push({ item, file })
        }
    }

    return Array.from(groups.values()).sort((left, right) => left.fileName.localeCompare(right.fileName))
}

function fileGroupHeadingId(key) {
    return `quality-file-${String(key || 'file').replace(/[^a-z0-9_-]+/gi, '-')}`
}

const issueGridSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
    gap: 1.5,
    alignItems: 'start',
    '& > *': { minWidth: 0, height: 'fit-content' }
}

const fileGroupSx = {
    p: { xs: 1.5, md: 2 },
    borderRadius: 3,
    borderColor: '#cfdfe7',
    backgroundColor: '#f7fafc'
}

function formatSavedTime(value) {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
